import { ComboboxItem, isOptionsGroup } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { BaseQueryFn, TypedUseLazyQuery } from '@reduxjs/toolkit/query/react';
import { ApiResponse, AxiosRequestConfigExtended, OptionsGroup } from '@vision/ui/interfaces';
import { ensureArray, insertIfObject, uniqueArray } from '@vision/ui/utils';
import type { AxiosResponse } from 'axios';
import qs from 'qs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect, useEffectOnce, useFirstMountState } from 'react-use';
import {
  hasLazyLoadSelectItemValue,
  hasNewLazyLoadSelectItems,
  mergeLazyLoadSelectItems,
} from './useLazyLoadSelect.utils';

type SingleSelectItemMapper<ResponseType> = (item: ResponseType) => ComboboxItem | OptionsGroup;
type MultiSelectItemMapper<ResponseType> = (items: ResponseType[]) => Array<ComboboxItem | OptionsGroup>;
type LazyLoadResponseType<ResponseType> = Array<ResponseType & { id: string }>;
const DEFAULT_VALUES_BATCH_SIZE = 50;
const DEFAULT_VALUES_MAX_ATTEMPTS = 2;

export interface UseLazyLoadSelectOptions<RequestType, ResponseType> {
  searchKey?: string;
  filterKey?: string;
  apiRequestParameters?: Omit<RequestType, 'query'>;
  disabledCondition?: (item: ComboboxItem) => boolean;
  multiSelectItemMapper?: MultiSelectItemMapper<ResponseType>;
  singleSelectItemMapper?: SingleSelectItemMapper<ResponseType>;
  useLazyApiQueryFunction: TypedUseLazyQuery<
    ApiResponse<LazyLoadResponseType<ResponseType>>,
    RequestType,
    BaseQueryFn<AxiosRequestConfigExtended, unknown, AxiosResponse<any, any>>
  >;
  defaultValues?: string | string[];
  resetChangeRequestParameters?: boolean;
  reuseCachedData?: boolean;
  onDefaultValuesLoaded?: (data: ResponseType[], requestedValues: string[]) => void;
}

export function useLazyLoadSelect<RequestType, ResponseType>({
  searchKey = 'name',
  filterKey = 'ids',
  apiRequestParameters,
  disabledCondition,
  multiSelectItemMapper,
  singleSelectItemMapper,
  useLazyApiQueryFunction,
  defaultValues,
  resetChangeRequestParameters = false,
  reuseCachedData = false,
  onDefaultValuesLoaded,
}: UseLazyLoadSelectOptions<RequestType, ResponseType>) {
  const isFirstMount = useFirstMountState();
  const [data, setData] = useState<Array<ComboboxItem | OptionsGroup>>([]);
  const [allData, setAllData] = useState<LazyLoadResponseType<ResponseType>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [allPagesLoaded, setAllPagesLoaded] = useState(false);
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);

  const [getQueryFn, { data: queryResponse, isFetching: isLoading }] = useLazyApiQueryFunction();
  const [getQueryDataFn, { isFetching: isDefaultDataLoading }] = useLazyApiQueryFunction();
  const [getQueryFilterFn, { data: queryFilteredResponse, isFetching: isFilterQueryLoading }] =
    useLazyApiQueryFunction();
  const [isLoadingDefaultValues, setIsLoadingDefaultValues] = useState(true);
  const requestedDefaultValues = useRef(new Set<string>());
  const failedDefaultValues = useRef(new Set<string>());
  const defaultValuesRequestId = useRef(0);
  const latestDefaultValues = useRef(defaultValues);
  const onDefaultValuesLoadedRef = useRef(onDefaultValuesLoaded);
  onDefaultValuesLoadedRef.current = onDefaultValuesLoaded;
  const defaultValueKey = ensureArray(defaultValues).join(',');

  useEffect(() => {
    latestDefaultValues.current = defaultValues;
  }, [defaultValues]);

  useEffect(
    () => () => {
      defaultValuesRequestId.current += 1;
    },
    [],
  );

  const handleMapSelectItems = (response: LazyLoadResponseType<ResponseType>) => {
    if (singleSelectItemMapper) {
      return response.map(singleSelectItemMapper);
    }

    if (multiSelectItemMapper) {
      return multiSelectItemMapper(response);
    }

    // Default mapper
    return response.map((item) => ({
      value: item.id,
      label: item.id,
    }));
  };

  const allDataSelectItems = useMemo(() => handleMapSelectItems(allData), [allData]);

  const dataSelectItemsWithDisabledCondition: Array<ComboboxItem | OptionsGroup> = useMemo(() => {
    if (disabledCondition) {
      return data.map((item) => {
        const isGroup = isOptionsGroup(item);
        if (isGroup) {
          return {
            ...item,
            items: ensureArray(item.items).map((child) => ({
              ...child,
              disabled: disabledCondition(child) ?? false,
            })),
          };
        }
        return {
          ...item,
          disabled: disabledCondition(item) ?? false,
        };
      });
    }

    return data;
  }, [disabledCondition, data]);

  const handleLoadMore = () => {
    if (searchQuery) {
      if (queryFilteredResponse?.meta?.pagination?.current === queryFilteredResponse?.meta?.pagination?.total_pages) {
        return;
      }
      const currentPage = queryFilteredResponse?.meta?.pagination?.current ?? 1;
      handleLoadMoreFilterRequest(currentPage + 1);
    } else {
      if (queryResponse?.meta?.pagination?.current === queryResponse?.meta?.pagination?.total_pages) {
        return;
      }
      const currentPage = queryResponse?.meta?.pagination?.current ?? 1;
      handleLoadMoreQueryRequest(currentPage + 1);
    }
  };

  const handleLoadMoreQueryRequest = (page: number) => {
    getQueryFn(
      {
        query: qs.stringify({
          page,
          per_page: 20,
        }),
        ...apiRequestParameters,
      } as RequestType,
      reuseCachedData,
    );
  };

  const handleLoadMoreFilterRequest = (page: number) => {
    getQueryFilterFn(
      {
        query: qs.stringify({
          page,
          per_page: 20,
          ...insertIfObject(!!searchQuery, {
            [searchKey]: searchQuery,
          }),
        }),
        ...apiRequestParameters,
      } as RequestType,
      reuseCachedData,
    );
  };

  const handleGetData = (value: Array<string> | string) => {
    const values = [...new Set(ensureArray(value))];
    const selectedValues = ensureArray(defaultValues);
    const requestId = ++defaultValuesRequestId.current;
    setIsLoadingDefaultValues(true);

    const loadDefaultValues = async (): Promise<void> => {
      const loadedDefaultValues: LazyLoadResponseType<ResponseType> = [];
      const completedDefaultValues: string[] = [];

      try {
        for (let index = 0; index < values.length; index += DEFAULT_VALUES_BATCH_SIZE) {
          const batch = values.slice(index, index + DEFAULT_VALUES_BATCH_SIZE);
          let response: ApiResponse<LazyLoadResponseType<ResponseType>> | undefined;

          for (let attempt = 0; attempt < DEFAULT_VALUES_MAX_ATTEMPTS; attempt += 1) {
            try {
              response = await getQueryDataFn(
                {
                  query: qs.stringify({
                    [filterKey]: batch.join(','),
                    per_page: DEFAULT_VALUES_BATCH_SIZE,
                  }),
                  ...apiRequestParameters,
                } as RequestType,
                reuseCachedData,
              ).unwrap();
              break;
            } catch {
              if (requestId !== defaultValuesRequestId.current) return;

              if (attempt === DEFAULT_VALUES_MAX_ATTEMPTS - 1) {
                batch.forEach((item) => failedDefaultValues.current.add(item));
              }
            }
          }

          if (requestId !== defaultValuesRequestId.current) return;
          if (!response) continue;

          batch.forEach((item) => requestedDefaultValues.current.add(item));
          batch.forEach((item) => failedDefaultValues.current.delete(item));
          loadedDefaultValues.push(...response.data);
          completedDefaultValues.push(...batch);
          const loadedItems = handleMapSelectItems(response.data);
          setAllData((prev) => uniqueArray(prev.concat(response.data), 'id'));
          setData((prev) => mergeLazyLoadSelectItems(prev, loadedItems));
        }

        const currentValues = ensureArray(latestDefaultValues.current);
        const selectionsUnchanged =
          currentValues.length === selectedValues.length &&
          currentValues.every((item) => selectedValues.includes(item));

        if (!selectionsUnchanged) {
          // Revalidate remaining selections with the current consumer callback.
          currentValues.forEach((item) => requestedDefaultValues.current.delete(item));
        } else if (completedDefaultValues.length === values.length) {
          // Consumers must receive every batch before reconciling their selections.
          onDefaultValuesLoadedRef.current?.(uniqueArray(loadedDefaultValues, 'id'), completedDefaultValues);
        }
      } finally {
        if (requestId === defaultValuesRequestId.current) setIsLoadingDefaultValues(false);
      }
    };

    void loadDefaultValues();
  };

  const handleSearch = (query: string) => {
    // If there is no query or all pages are loaded, do not send a request to the API, the Select component can already search for the value written within itself.
    if (!query || allPagesLoaded) {
      // If there is no filtering, show all the data retrieved so far
      setData(allDataSelectItems);
      return;
    }

    getQueryFilterFn(
      {
        query: qs.stringify({
          page: 1,
          per_page: 20,
          ...insertIfObject(!!query, {
            [searchKey]: query,
          }),
        }),
        ...apiRequestParameters,
      } as RequestType,
      reuseCachedData,
    );
  };

  const contextValue = useMemo(
    () => ({
      loadMore: handleLoadMore,
      currentPage: queryResponse?.meta?.pagination?.current,
      totalPage: queryResponse?.meta?.pagination?.total_pages,
    }),
    [handleLoadMore, queryResponse],
  );

  useEffect(() => {
    if (!queryResponse) {
      return;
    }

    // If there is no next page, all pages have been fetched
    if (!queryResponse.meta.pagination.next) {
      setAllPagesLoaded(true);
    }

    // Filter the received data and if it already exists, fetch the next page
    const list = handleMapSelectItems(queryResponse.data);

    // If there is no data in the received items, fetch the next page
    if (hasNewLazyLoadSelectItems(data, list)) {
      // Store the received data in a separate "allData" value, used to display the previously fetched data when the filter is removed
      setAllData((prev) => uniqueArray(prev.concat(queryResponse.data), 'id'));
      setData((prev) => mergeLazyLoadSelectItems(prev, list));
    } else if (queryResponse.meta.pagination.next && !allPagesLoaded) {
      handleLoadMoreQueryRequest(queryResponse.meta.pagination.current + 1);
    }
  }, [queryResponse]);

  useEffect(() => {
    if (!queryFilteredResponse) {
      return;
    }

    const list = handleMapSelectItems(queryFilteredResponse.data);

    setAllData((prev) => uniqueArray(prev.concat(queryFilteredResponse.data), 'id'));
    setData((prev) => mergeLazyLoadSelectItems(prev, list));
  }, [queryFilteredResponse]);

  useEffect(() => {
    handleSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const fetchInitialData = (values: Array<string> | string, availableData: LazyLoadResponseType<ResponseType> = []) => {
    // When the component is mounted, fetch the first page
    handleLoadMoreQueryRequest(1);

    const missingValues = ensureArray(values).filter((value) => !availableData.some((item) => item.id === value));
    // Fetch only selected values that have not already been loaded.
    if (missingValues.length > 0) {
      setIsLoadingDefaultValues(true);
      handleGetData(missingValues);
    } else {
      setIsLoadingDefaultValues(false);
    }
  };

  useEffectOnce(() => {
    fetchInitialData(defaultValues);
  });

  useEffect(() => {
    failedDefaultValues.current.clear();
    const currentDefaultValues = new Set(ensureArray(defaultValues));
    requestedDefaultValues.current.forEach((value) => {
      if (!currentDefaultValues.has(value)) requestedDefaultValues.current.delete(value);
    });
  }, [defaultValueKey]);

  useEffect(() => {
    if (isLoadingDefaultValues) {
      return;
    }
    // If the defaultValues are not in dataSelectItemsWithDisabledCondition, request the missing ones
    const missingDefaultValues = ensureArray(defaultValues).filter(
      (value) =>
        !requestedDefaultValues.current.has(value) &&
        !failedDefaultValues.current.has(value) &&
        !hasLazyLoadSelectItemValue(dataSelectItemsWithDisabledCondition, value),
    );

    if (missingDefaultValues.length > 0) {
      setIsLoadingDefaultValues(true);
      handleGetData(reuseCachedData ? missingDefaultValues : ensureArray(defaultValues));
    }
  }, [defaultValues, isLoadingDefaultValues]);

  useDeepCompareEffect(() => {
    // When the parameters change, the requests should start from scratch. This is optional.
    if (resetChangeRequestParameters && !isFirstMount) {
      defaultValuesRequestId.current += 1;
      requestedDefaultValues.current.clear();
      failedDefaultValues.current.clear();
      const selectedValues = ensureArray(defaultValues);
      const selectedData = reuseCachedData ? allData.filter((item) => selectedValues.includes(item.id)) : [];

      setAllData(selectedData);
      setData(handleMapSelectItems(selectedData));
      fetchInitialData(defaultValues, selectedData);
      setAllPagesLoaded(false);
    }
  }, [resetChangeRequestParameters, reuseCachedData, apiRequestParameters]);

  return {
    allData,
    allDataSelectItems,
    contextValue,
    dataSelectItemsWithDisabledCondition,
    isFilterQueryLoading,
    isLoading,
    setSearchQuery,
    allPagesLoaded,
    isDefaultDataLoading,
    isLoadingDefaultValues,
  };
}
